import type { DateChipOption } from '@openx/components-core/lib/DateRangePickerField/components/DateRangeChips.tsx';
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  getDate,
  isMonday,
  isThursday,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { dateRangeChipOptionId } from '../constants.ts';

const createConditionalOption = (condition: boolean, option: DateChipOption): DateChipOption[] => {
  return condition ? [] : [option];
};

export const useDateRangeChipOptions = (maxDate: Date): DateChipOption[] => {
  const { t } = useTranslation();
  const currentTime = new Date();

  return [
    {
      id: dateRangeChipOptionId.LAST_QUARTER,
      label: t('Last quarter'),
      startDate: startOfQuarter(subQuarters(currentTime, 1)),
      endDate: endOfQuarter(subQuarters(currentTime, 1)),
    },
    {
      id: dateRangeChipOptionId.LAST_MONTH,
      label: t('Last month'),
      startDate: startOfMonth(subMonths(currentTime, 1)),
      endDate: endOfMonth(subMonths(currentTime, 1)),
    },
    ...createConditionalOption(
      getDate(currentTime) === 1, // This month makes no sense to show on first day of the month (we don't have data yet)
      {
        id: dateRangeChipOptionId.THIS_MONTH,
        label: t('This month'),
        startDate: startOfMonth(currentTime),
        endDate: maxDate,
      }
    ),
    {
      id: dateRangeChipOptionId.LAST_WEEK,
      label: t('Last week'),
      startDate: startOfWeek(subWeeks(currentTime, 1), { weekStartsOn: 1 }),
      endDate: endOfWeek(subWeeks(currentTime, 1), { weekStartsOn: 1 }),
    },
    ...createConditionalOption(
      isMonday(currentTime) || isThursday(currentTime), // This week makes no sense to show on Monday (we don't have data yet) or Thursday (redundant with last 3 days)
      {
        id: dateRangeChipOptionId.THIS_WEEK,
        label: t('This week'),
        startDate: startOfWeek(currentTime, { weekStartsOn: 1 }),
        endDate: maxDate,
      }
    ),
    ...createConditionalOption(
      isMonday(currentTime), // This week makes no sense to show on Monday (redundant with last week)
      {
        id: dateRangeChipOptionId.LAST_7_DAYS,
        label: t('Last 7 days'),
        startDate: subDays(currentTime, 7),
        endDate: maxDate,
      }
    ),
    {
      id: dateRangeChipOptionId.LAST_3_DAYS,
      label: t('Last 3 days'),
      startDate: subDays(currentTime, 3),
      endDate: maxDate,
    },
    {
      id: dateRangeChipOptionId.YESTERDAY,
      label: t('Yesterday'),
      startDate: subDays(currentTime, 1),
      endDate: subDays(currentTime, 1),
    },
  ];
};
